import { useMutation, useQuery } from '@apollo/client';
import { message } from 'antd';
import {
  ApplicantDetailsHeaderContainerApplicantUserDocument,
  ApplicantDetailsHeaderContainerApplicantUserFieldsFragment,
  ApplicantDetailsHeaderContainerApplicantUserUpdateDocument,
  ApplicantDetailsHeaderContainerStaffUserCurrentDocument,
  ApplicantDetailsHeaderContainerStaffUserFieldsFragment
} from '../../../generated';
import { useOid } from '../../../hooks/useOid';
import { ComponentQueryLoader } from '../component-query-loader';
import { getStaffUserCurrentQueryOptionsWithOidAndCacheEnabled } from '../utils/queryOptions';
import { ApplicantDetailsHeader } from './applicant-details-header';

interface ApplicantDetailsHeaderContainerProps {
  applicantId: string;
}
export const ApplicantDetailsHeaderContainer: React.FC<ApplicantDetailsHeaderContainerProps> = (props) => {
  const oid = useOid();
  const staffUserCurrentQueryOptions = getStaffUserCurrentQueryOptionsWithOidAndCacheEnabled(oid);

  const {
    data: staffUserCurrentData,
    loading: staffUserCurrentLoading,
    error: staffUserCurrentError
  } = useQuery(ApplicantDetailsHeaderContainerStaffUserCurrentDocument, staffUserCurrentQueryOptions);

  const { data, loading, error } = useQuery(ApplicantDetailsHeaderContainerApplicantUserDocument, {
    variables: {
      applicantUserId: props.applicantId
    }
  });
  const [updateApplicant, { loading: updateApplicantLoading }] = useMutation(ApplicantDetailsHeaderContainerApplicantUserUpdateDocument, {
    variables: {
      input: {
        id: props.applicantId
      }
    }
  });

  const onBlockUnblockAccess = async (isAccessBlocked: boolean) => {
    const data = await updateApplicant({
      variables: {
        input: {
          applicantUserDetails: {
            accessBlocked: isAccessBlocked
          },
          id: props.applicantId
        }
      }
    });

    if (data.data?.applicantUserUpdate?.status.success && data.data?.applicantUserUpdate?.applicantUser?.accessBlocked) {
      message.success(`Access has been restricted for ${data.data.applicantUserUpdate.applicantUser.displayName}`);
    }

    if (data.data?.applicantUserUpdate?.status.success && !data.data?.applicantUserUpdate?.applicantUser?.accessBlocked) {
      message.success(`Access has been restored for ${data.data.applicantUserUpdate.applicantUser?.displayName}`);
    }

    if (!data.data?.applicantUserUpdate?.status.success) {
      message.error('Failed to update access');
    }
  };

  const handleTagsChange = async (newTags: string[]): Promise<boolean> => {
    try {
      const data = await updateApplicant({
        variables: {
          input: {
            applicantUserDetails: {
              tags: newTags
            },
            id: props.applicantId
          }
        }
      });

      if (data.data?.applicantUserUpdate?.status.success) {
        message.success('Tags updated successfully');
        return true;
      }
    } catch (error) {
      message.error(`Error saving tags: ${JSON.stringify(error)}`);
    }

    return false;
  };

  return (
    <ComponentQueryLoader
      error={error || staffUserCurrentError}
      loading={loading || staffUserCurrentLoading}
      hasData={data?.applicantUser && staffUserCurrentData?.staffUserCurrent?.role}
      hasDataComponent={
        <ApplicantDetailsHeader
          isUpdating={updateApplicantLoading}
          onTagsChange={handleTagsChange}
          applicantDetails={data?.applicantUser as ApplicantDetailsHeaderContainerApplicantUserFieldsFragment}
          onBlockUnblockAccess={onBlockUnblockAccess}
          staffRolePermissionData={staffUserCurrentData?.staffUserCurrent as ApplicantDetailsHeaderContainerStaffUserFieldsFragment}
        />
      }
    />
  );
};
